import { Fragment, useEffect, useState } from "react";
import { Badge, Button, Collapse, Flex, Input, message, Popconfirm, Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { CheckOutlined, CloseOutlined, QuestionCircleOutlined, } from '@ant-design/icons';
import { mdlReservation } from "../../models/domain/Reservation";
import { PdfIcon } from "../common/CustomIcons";
import { SFModal } from "../elements/SFModal";
import { enmPdfRendererType } from "../../models/enums/PdfRenderer";
import { useAppDispatch, useAppSelector } from "../../store/Hooks";
import { mdlPDFRendererProps } from "../../models/ui-models/PDFRendererProps";
import { enmExportStatus } from "../../models/enums/ExportStatus";
import _ from 'lodash';
import { toast } from "react-toastify";
import { useMutation } from "react-query";
import { mdlReservationConfirmRequest } from "../../models/service-models/reservation/ReservationConfirmRequest";
import { ReservationService } from "../../services/ReservationService";
import { ObjectHelper } from "../../utils/ObjectHelper";
import { enmConfirmationStatus } from "../../models/enums/ConfirmationStatus";
import { setPdfExportObject } from "../../store/SiteSlice";
import { enmPdfExportType } from "../../models/enums/PdfExportType";
import { InputHelper } from "../../utils/InputHelper";
import { mdlReservationPrintDetail } from "../../models/ui-models/ReservationPrintDetail";

export type ReservationConfirmProps = {
    reservations?: mdlReservation[];
    closeCallBack?: Function;
    open?: boolean;
    callback?: Function;
    fromEmail?: boolean;
}

export const ReservationConfirm = (props: ReservationConfirmProps) => {
    const { t } = useTranslation();
    const [openSameNoteModal, setOpenSameNoteModal] = useState<boolean>(false);
    const [confirmReservations, setConfirmReservations] = useState<Array<mdlReservation>>();
    const [allNote, setAllNote] = useState<string | undefined>();
    const dispatch = useAppDispatch();
    const exportStatus = useAppSelector(x => x.site.exportStatus);
    const [messageApi, contextHolder] = message.useMessage();
    const loggedUserHotels = useAppSelector(x => x.site.userHotels);

    useEffect(() => {
        setConfirmReservations(_.cloneDeep(props.reservations));
    }, [props.reservations]);

    //#region EXPORT HANDLERS
    const exportToPDF = (type: enmPdfExportType, reservations: Array<mdlReservation>) => {
        var exportProps: mdlPDFRendererProps = {
            params: { reservations: reservations },
            rendererType: enmPdfRendererType.ReservationVoucher,
            type: type
        }
        dispatch(setPdfExportObject(exportProps));
    }

    const handleExportToPdfClick = (reservation: mdlReservation, index: number) => {
        exportToPDF(enmPdfExportType.PDF, [reservation]);
    }

    const { mutateAsync: getMultiResDetail, isLoading: resDetailLoading, data: resDetail } = useMutation(
        (request: mdlReservationPrintDetail) => ReservationService.MultiDetail(request.request!),
        {
            onMutate(variables) {
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    if (variables.callbackFn) {
                        variables.callbackFn(data.body?.reservations);
                    }
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const handleExportToPDFALLClick = async () => {
        if (confirmReservations) {
            getMultiResDetail({
                request: {
                    reservations: confirmReservations!.map(h => Object.assign(h!, {
                        reservationId: h.reservationId!,
                        agencyId: h.agencyId!,
                        paxId: h.paximumId!
                    }))
                }
                , callbackFn: (reservations?: Array<mdlReservation>) => exportToPDF(enmPdfExportType.PDF, reservations!)
            });
        }
        else {
            toast.error(t("common.noReservationSelected"));
        }
    }

    const handlePrintAfterSuccess = () => {
        var cloneReservations = _.cloneDeep(confirmReservations);
        _.each(cloneReservations, function (res) {
            res.confirmationStatus = enmConfirmationStatus.No
        })
        if (cloneReservations) {
            exportToPDF(enmPdfExportType.PDF, cloneReservations)
            props.callback && props.callback();
        }
    }
    //#endregion

    //#region NOTE HANDLERS
    const handleConfirmNoChange = (value: any, index: number) => {
        setConfirmReservations(prevConfirmReservations =>
            prevConfirmReservations?.map((confirmReservation, resIndex) => (
                resIndex == index ?
                    { ...confirmReservation, confirmationNo: value }
                    : confirmReservation
            )
            ));
    }

    const handleRemoveAllNotes = () => {
        if (confirmReservations) {
            setConfirmReservations(prevConfirmReservations =>
                prevConfirmReservations?.map(confirmReservation =>
                ({
                    ...confirmReservation, confirmeNote: undefined
                })
                )
            );
        }
    }

    const handleAllNoteConfirm = () => {
        if (confirmReservations && allNote) {
            setConfirmReservations(prevConfirmReservations =>
                prevConfirmReservations?.map(confirmReservation =>
                ({
                    ...confirmReservation, confirmeNote: allNote
                })
                )
            );
            setOpenSameNoteModal(false);
            messageApi.open({
                type: "success",
                content: t("resConfirm.allNotesDone"),
                duration: 1000,
            });
        }
    }

    const handleNoteChange = (value: string, index: number, toAll: boolean = false) => {
        if (toAll) {
            setAllNote(value);
        } else {
            if (confirmReservations) {
                setConfirmReservations(prevConfirmReservations =>
                    prevConfirmReservations?.map((confirmReservation, resIndex) =>
                        index === resIndex
                            ? {
                                ...confirmReservation,
                                confirmeNote: value
                            }
                            : confirmReservation
                    )
                );
            }
            else
                toast.error(t("resConfirm.reservationNoteArrayError"));
        }
    }
    //#endregion

    const checkConfirmNoRequirement = (reservations: Array<mdlReservation>) => {
        var finalResult = true;
        _.each(reservations, function (reservation) {
            var control = false;
            var foundHotel = _.find(loggedUserHotels, function (hotel) {
                return hotel.id == reservation.hotelId
            });
            if (foundHotel) {
                var rezNoRequiredSetting = _.find(foundHotel.hotelWebParameters, function (param) {
                    return param.parameterName == "ResNoZorunlu"
                });
                if (rezNoRequiredSetting) {
                    if (rezNoRequiredSetting.value1 == "TRUE")
                        control = true;
                    else
                        control = false;
                }
            }
            if (control == true) {
                if (InputHelper.isNullOrUndefinedOrEmpty(reservation.confirmationNo)) {
                    toast.error(t("resConfirm.confirmationNoRequired") + " " + reservation.hotelName);
                    finalResult = false;
                }
            }
        })
        return finalResult;
    }

    //#region SEND HANDLERS
    const handleSendClick = () => {
        if (confirmReservations) {
            if (checkConfirmNoRequirement(confirmReservations))
                sendConfirmation({
                    request: {
                        reservations: confirmReservations.map(r => ({
                            agencyId: r.agencyId,
                            paximumId: r.paximumId,
                            reservationId: r.reservationId,
                            note: r.confirmeNote
                        })),
                        fromEmail: props.fromEmail
                    },
                    printAll: false
                });
        }
    }

    const handleSendAndExportToPDFClick = () => {
        if (confirmReservations)
            if (checkConfirmNoRequirement(confirmReservations))
                sendConfirmation({
                    request: {
                        reservations: confirmReservations.map(r => ({
                            agencyId: r.agencyId,
                            paximumId: r.paximumId,
                            reservationId: r.reservationId,
                            note: r.confirmeNote
                        })),
                        fromEmail: props.fromEmail
                    },
                    printAll: true
                });
    }
    //#endregion

    //#region API CALLS
    const { mutateAsync: sendConfirmation, isLoading: mutateLoading, data: mutateData } = useMutation(
        (data: { request: mdlReservationConfirmRequest, printAll: boolean }) => ReservationService.Confirm(data.request),
        {            
            onError: error => {
                toast.error(JSON.stringify(error));
            },
            onSuccess(data, variables, context) {
                if (data && data.header.success) {
                    if (variables.printAll) {
                        handlePrintAfterSuccess();
                    }
                    else {
                        props.callback && props.callback();
                    }
                }
                else {
                    if (data.header.success && data.body?.errors)
                        toast.error(_.first(data.body.errors));
                    else
                        toast.error(t(ObjectHelper.getErrorMessage(data.header.messages)));
                }
            },
        }
    );
    //#endregion

    return (
        <>
            {
                confirmReservations
                &&
                <Spin spinning={exportStatus != enmExportStatus.DONE || mutateLoading || resDetailLoading}>
                    {contextHolder}
                    <Badge.Ribbon text={t("resConfirm.badgeTitle")} color={"green"}>
                        {
                            <Fragment>
                                <div className="card card-bordered">
                                    <div className="card-inner">
                                        <h6>
                                            {`${t("resConfirm.totalReservation")}: ${confirmReservations?.length}`}
                                        </h6>
                                    </div>
                                </div>
                                {
                                    confirmReservations.map((reservation: mdlReservation, index: number) => (
                                        <Fragment key={index}>
                                            <div className="card card-bordered">
                                                <div className="card-inner">
                                                    <div className="team">
                                                        <div className="card-inner">
                                                            <div className="project-head">
                                                                <div>
                                                                </div>
                                                                <div>
                                                                    <Button onClick={() => handleExportToPdfClick(reservation, index)} loading={exportStatus != enmExportStatus.DONE} type="primary" icon={<PdfIcon />} size="small">
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.agency")}</span>
                                                                    <span>{reservation.agencyName}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.hotel")}</span>
                                                                    <span>{reservation.hotelName}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.operator")}</span>
                                                                    <span>{reservation.operator}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.voucher")}</span>
                                                                    <span>{reservation.voucherNo}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.checkIn")}</span>
                                                                    <span>{dayjs(reservation.checkIn).format("DD.MM.YYYY")}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.checkOut")}</span>
                                                                    <span>{dayjs(reservation.checkOut).format("DD.MM.YYYY")}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.accomStyle")}</span>
                                                                    <span>{reservation.roomName}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.roomCount")}</span>
                                                                    <span>{reservation.roomCount}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.roomType")}</span>
                                                                    <span>{reservation.roomTypeName}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.totalPax")}</span>
                                                                    <span>{reservation.pax}</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.hotelConfirmation")}</span>
                                                                    <span>-</span>
                                                                </div>
                                                                <div className="col-sm-6 col-md-1 col-lg-1">
                                                                    <span className="sub-text">{t("resConfirm.confirmationNo")}</span>
                                                                    {
                                                                        reservation.confirmationStatus != enmConfirmationStatus.No && reservation.confirmationStatus != enmConfirmationStatus.Yes
                                                                            ?
                                                                            <Input type="number" value={reservation.confirmationNo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleConfirmNoChange(e.target.value, index)} />
                                                                            :
                                                                            <span>{reservation.confirmationNo}</span>
                                                                    }
                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-sm-6 col-md-4 col-lg-4">
                                                                    <span className="sub-text">{t("resConfirm.price")}</span>
                                                                    {
                                                                        reservation.soldPrice ?
                                                                            <span>{`${reservation.soldPrice} ${reservation.soldCurrency}`}</span>
                                                                            :
                                                                            <>-</>
                                                                    }

                                                                </div>
                                                                <div className="col-sm-6 col-md-8 col-lg-8">
                                                                    <span className="sub-text">{t("resConfirm.extraServices")}</span>
                                                                    <span>{reservation.extraServicesInfo}</span>
                                                                </div>
                                                            </div>
                                                            <div className="row">
                                                                <div className="col-sm-12 col-md-12 col-lg-12">
                                                                    <Collapse
                                                                        items={[{
                                                                            key: '1', label: t("resConfirm.note"),
                                                                            children: <Input.TextArea rows={5} value={reservation.confirmeNote} maxLength={500} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleNoteChange(e.currentTarget.value, index)} />
                                                                        }]}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Fragment>
                                    ))
                                }
                                <div className="card">
                                    <Flex gap="small" wrap justify="space-between">
                                        <Flex gap="small" wrap justify="flex-start">
                                            {
                                                props.reservations && props.reservations.length > 1
                                                &&
                                                <>
                                                    <Button color="primary" size="middle" onClick={() => setOpenSameNoteModal(!openSameNoteModal)} variant="outlined">
                                                        {t("resConfirm.addSameNoteToAll")}
                                                    </Button>
                                                    <Popconfirm
                                                        title={t("common.deleteWarning")}
                                                        description={t("resConfirm.removeAllNotesDescription")}
                                                        okText={t("common.okConfirm")}
                                                        cancelText={t("common.cancelConfirm")}
                                                        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
                                                        onConfirm={handleRemoveAllNotes}
                                                    >
                                                        <Button color="primary" size="middle" variant="outlined">
                                                            {t("resConfirm.clearAllNotes")}
                                                        </Button>
                                                    </Popconfirm>
                                                    <Button type="primary" size="middle" loading={exportStatus != enmExportStatus.DONE} onClick={handleExportToPDFALLClick} icon={<PdfIcon />} >
                                                        {t("resConfirm.printAll")}
                                                    </Button>
                                                </>
                                            }


                                        </Flex>
                                        <Flex gap="small" wrap justify="flex-end">
                                            <Button type="primary" className="btn-success" onClick={handleSendClick} icon={<CheckOutlined />} size="middle">
                                                {t("resConfirm.send")}
                                            </Button>
                                            <Button type="primary" className="btn-success" onClick={handleSendAndExportToPDFClick} icon={<CheckOutlined />} size="middle">
                                                {t("resConfirm.sendAndPrint")}
                                            </Button>
                                            <Button type="primary" danger onClick={() => props.closeCallBack && props.closeCallBack()} icon={<CloseOutlined />} size="middle">
                                                {t("resConfirm.close")}
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </div>
                            </Fragment>

                        }
                    </Badge.Ribbon >
                </Spin>

            }
            <SFModal
                key={"res-confirm-all-note-model"}
                width={window.innerWidth * 40 / 100}
                open={openSameNoteModal}
                toggler={() => { setOpenSameNoteModal(!openSameNoteModal) }}
                body={<Input.TextArea rows={5} maxLength={500} value={allNote || ""} onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => handleNoteChange(event.target.value, 0, true)} />}
                footer={() =>
                    <>
                        <Button type="primary" className="btn-success" onClick={handleAllNoteConfirm} size="middle">
                            {t("common.ok")}
                        </Button>
                        <Button type="primary" danger onClick={() => setOpenSameNoteModal(false)} icon={<CloseOutlined />} size="middle">
                            {t("common.close")}
                        </Button>
                    </>
                }
                title={t("resConfirm.note")}
            />
        </>
    )
}